import { StateCreator } from 'zustand';
import { Store } from 'src/stores/store.interface';
import { TaskDefinitionStore } from './taskDefinitionStore.interface';
import { validateTaskDefinition } from 'src/components/createWorklow/updateTaskDefinition/updateTaskDefinitionUtils';
import { ReactFlowNodeStatus } from 'src/components/createWorklow/customNode/customNodeEnums';

export const defaultTaskDefinition = {
	asynchronous: false,
	averageExecutionTime: {
		days: 0,
		hours: 0,
		minutes: 0,
	},
	comment: '',
	createdBy: '',
	creationTime: 0,
	owner: '',
	taskDescription: '',
	entityTypeSupported: '',
	lastUpdateTime: 0,
	lastUpdatedBy: '',
	genericAPITaskName: '',
	regionalized: false,
	stage: '',
	latest: true,
	status: '',
	taskParameter: '',
	taskDefinitionVersion: 1,
	notificationTask: false,
	individualSIMTRequired: false,
	batchExecutionRequired: false,
	taskId: '',
	taskInputs: [],
	taskName: '',
	taskOutputs: [],
	taskType: '',
	conditionalAttributes: [
		{ operator: 'EQUALS', attributeName: '', attributeValue: '' },
	],
};

export const defaultTaskDefinitionStore = {
	isFirstSubmissionDone: false,
	activeReactFlowNodeId: '',
	activeTaskDefinition: { ...defaultTaskDefinition },
};

const taskDefinitionStore: StateCreator<Store, [], [], TaskDefinitionStore> = (
	set,
	get
) => ({
	...defaultTaskDefinitionStore,
	setActiveTaskDefinitionStore: (
		newActiveTaskDefinition,
		activeReactFlowNodeId,
		isFirstSubmissionDone
	) => {
		set(() => ({
			activeTaskDefinition: newActiveTaskDefinition,
			activeReactFlowNodeId,
			isFirstSubmissionDone,
		}));
	},
	resetActiveTaskDefinitionStore: () => {
		set(() => ({
			activeTaskDefinition: defaultTaskDefinition,
			activeReactFlowNodeId: '',
			isFirstSubmissionDone: false,
		}));
	},
	setIsFirstSubmissionDone: (isFirstSubmissionDone) => {
		set(() => ({ isFirstSubmissionDone }));
	},
	updateActiveTaskDefinition: (key, value) => {
		set((state) => {
			const newActiveTaskDefinition = { ...state.activeTaskDefinition };

			// Convert string boolean values to actual booleans for boolean fields
			const booleanFields = ['batchExecutionRequired', 'notificationTask', 'individualSIMTRequired', 'regionalized', 'asynchronous'];
			if (booleanFields.includes(key) && typeof value === 'string') {
				newActiveTaskDefinition[key] = value === 'true';
			} else {
				newActiveTaskDefinition[key] = value;
			}

			return { activeTaskDefinition: newActiveTaskDefinition };
		});
	},
	setConditionalAttribute: (attributesList) => {
		set((state) => ({
			activeTaskDefinition: {
				...state.activeTaskDefinition,
				conditionalAttributes: attributesList,
			},
		}));
	},
	setTaskInputs: (updatedTaskInputs) => {
		set((state) => ({
			activeTaskDefinition: {
				...state.activeTaskDefinition,
				taskInputs: updatedTaskInputs,
			},
		}));
	},
	submitTaskDefinition: (userName) => {
		const activeTaskDefinition = get().activeTaskDefinition;
		const activeReactFlowNodeId = get().activeReactFlowNodeId;
		const updateReactFlowNodeDetails = get().updateReactFlowNodeDetails;
		const reactFlowInstance = get().reactFlowInstance;
		const activeNode = reactFlowInstance?.getNode ? reactFlowInstance.getNode(activeReactFlowNodeId) : null;

		if (activeNode && activeNode.data && activeNode.data.taskDefinition) {
			const errors = validateTaskDefinition(activeTaskDefinition);

			if (errors.length) {
				if (
					activeNode.data.nodeAdditionalProps.status !==
					ReactFlowNodeStatus.CREATION_SUCCESS
				) {
					updateReactFlowNodeDetails(activeReactFlowNodeId, {
						status: ReactFlowNodeStatus.CREATION_ERROR,
					});
					get().setIsFirstSubmissionDone(true);
				}

				return;
			}

			const updateNodeAdditionalProps = {
				name: activeTaskDefinition.taskName,
				status: ReactFlowNodeStatus.CREATION_SUCCESS,
			};

			updateReactFlowNodeDetails(
				activeReactFlowNodeId,
				updateNodeAdditionalProps,
				{
					...activeTaskDefinition,
					lastUpdatedBy: userName,
					lastUpdateTime: Math.floor(new Date().getTime() / 1000),
				}
			);
		} else if (activeReactFlowNodeId) {
			const errors = validateTaskDefinition(activeTaskDefinition);

			if (errors.length) {
				get().setIsFirstSubmissionDone(true);
				return;
			}

			const updatedTask = {
				...activeTaskDefinition,
				lastUpdatedBy: userName,
				lastUpdateTime: Math.floor(new Date().getTime() / 1000),
			};

			const WFDefinition = get().WFDefinition;
			const updatedTaskDefinitions = WFDefinition.taskDefinitions.map((t) =>
				t.taskId === activeReactFlowNodeId ? updatedTask : t
			);

			get().setWFDefinition({
				...WFDefinition,
				taskDefinitions: updatedTaskDefinitions,
			});
		}

		get().resetActiveTaskDefinitionStore();
	},
});

export default taskDefinitionStore;
